import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, ChevronRight, X, Phone, Clock, PhoneCall, Share2, Building } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const STEPS = [
  {
    id: 'portal_created',
    label: 'Portal opprettet',
    description: 'Kontoen din er opprettet og klar.',
    icon: Building,
    alwaysDone: true,
    link: null,
  },
  {
    id: 'phone_added',
    label: 'Legg til ditt telefonnummer',
    description: 'Konfigurer videresendingsnummer for agenten.',
    icon: Phone,
    alwaysDone: false,
    link: '/agent-config',
  },
  {
    id: 'hours_set',
    label: 'Konfigurer åpningstider',
    description: 'Sett opp når AI-resepsjonisten er aktiv.',
    icon: Clock,
    alwaysDone: false,
    link: '/settings',
  },
  {
    id: 'agent_tested',
    label: 'Test din AI-resepsjonist',
    description: 'Ring nummeret ditt for å høre agenten i aksjon — widget på dashbordet.',
    icon: PhoneCall,
    alwaysDone: false,
    link: '/dashboard',
  },
  {
    id: 'number_shared',
    label: 'Del nummeret med kunder',
    description: 'Du er klar — gjør nummeret kjent for kundene dine.',
    icon: Share2,
    alwaysDone: false,
    link: null,
  },
];

const STORAGE_KEY = (companyId) => `onboarding_checklist_${companyId}`;
const DISMISSED_KEY = (companyId) => `onboarding_dismissed_${companyId}`;

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.company_id;

  const [checked, setChecked] = useState({});
  const [dismissed, setDismissed] = useState(false);
  const [isNew, setIsNew] = useState(false);

  // Check if this is a new account (< 7 days old) or has incomplete steps
  useEffect(() => {
    if (!companyId) return;

    // Check dismissed state
    const wasDismissed = localStorage.getItem(DISMISSED_KEY(companyId)) === 'true';
    if (wasDismissed) { setDismissed(true); return; }

    // Load saved step state
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY(companyId)) || '{}');
      setChecked(saved);
    } catch (_) {}

    // Check if account is new (< 7 days) — use user created_at if available
    const createdAt = user?.created_at;
    if (createdAt) {
      const daysSince = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
      setIsNew(daysSince < 7);
    } else {
      setIsNew(true); // Default show if we can't determine
    }

    // Fetch real onboarding state from backend (DB is source of truth)
    (async () => {
      try {
        const { data } = await api.get('/api/onboarding-status');
        const serverSteps = data?.steps || {};
        setChecked(prev => {
          const next = { ...prev, ...serverSteps };
          localStorage.setItem(STORAGE_KEY(companyId), JSON.stringify(next));
          return next;
        });
        // Re-evaluate isNew if backend gave a created_at
        if (data?.created_at) {
          const daysSince = (Date.now() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24);
          setIsNew(daysSince < 14);
        }
      } catch (_) {
        // Fallback: try the agent-config endpoint to at least mark phone/hours
        try {
          const { data } = await api.get('/api/agent-config');
          const updates = {};
          if (data?.transfer_number) updates.phone_added = true;
          if (data?.business_hours) updates.hours_set = true;
          setChecked(prev => {
            const next = { ...prev, ...updates };
            localStorage.setItem(STORAGE_KEY(companyId), JSON.stringify(next));
            return next;
          });
        } catch (_) {}
      }
    })();
  }, [companyId, user?.created_at]);

  const toggleStep = useCallback((id) => {
    if (id === 'portal_created') return; // always done
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(STORAGE_KEY(companyId), JSON.stringify(next));
      return next;
    });
    // Persist manual completions (e.g. number_shared) to backend so it survives logout/device switch
    if (id === 'number_shared') {
      api.post('/api/onboarding-status/dismiss-step', { step_id: id }).catch(() => {});
    }
  }, [companyId]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY(companyId), 'true');
    setDismissed(true);
  };

  const completedCount = STEPS.filter(s => s.alwaysDone || checked[s.id]).length;
  const totalCount = STEPS.length;
  const allDone = completedCount === totalCount;

  // Don't show if dismissed, account is old and not new, or all steps done (auto-dismiss)
  if (dismissed) return null;
  if (!isNew && !allDone) {
    // Still show if steps remain incomplete for new-ish accounts
  }
  if (allDone) {
    // Auto-dismiss after all done — show briefly
    return (
      <div className="bg-accent-green/10 border border-accent-green/30 rounded-xl p-5 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle size={20} className="text-accent-green" />
          <div>
            <p className="text-sm font-semibold text-white">Du er klar! 🎉</p>
            <p className="text-xs text-slate-400">AI-resepsjonisten din er fullt konfigurert og i drift.</p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-white transition-colors"
          aria-label="Skjul"
          title="Skjul"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-xl p-5 mb-6" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-base font-semibold text-white">Kom i gang 🚀</h3>
          <p className="text-xs text-slate-500 mt-0.5">{completedCount} av {totalCount} steg fullført</p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-white transition-colors p-1"
          title="Skjul"
          aria-label="Skjul"
          data-testid="onboarding-dismiss"
        >
          <X size={16} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-navy-700 rounded-full h-1.5 mb-5">
        <div
          className="bg-accent-green h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${(completedCount / totalCount) * 100}%` }}
        />
      </div>

      <div className="space-y-2">
        {STEPS.map((step) => {
          const done = step.alwaysDone || checked[step.id];
          const Icon = step.icon;
          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                done ? 'opacity-60' : 'hover:bg-navy-700/50 cursor-pointer'
              }`}
              onClick={() => !done && toggleStep(step.id)}
              data-testid={`onboarding-step-${step.id}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); toggleStep(step.id); }}
                className="flex-shrink-0 text-slate-400 hover:text-accent-green transition-colors"
                disabled={step.alwaysDone}
              >
                {done
                  ? <CheckCircle size={18} className="text-accent-green" />
                  : <Circle size={18} />
                }
              </button>

              <div className="w-7 h-7 rounded-md bg-navy-700 flex items-center justify-center flex-shrink-0">
                <Icon size={14} className={done ? 'text-accent-green' : 'text-slate-400'} />
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${done ? 'line-through text-slate-500' : 'text-white'}`}>
                  {step.label}
                </p>
                <p className="text-xs text-slate-500 truncate">{step.description}</p>
              </div>

              {!done && step.link && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(step.link); }}
                  className="flex-shrink-0 text-accent-blue hover:text-accent-blue-hover transition-colors"
                  title="Gå til"
                >
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
